import { Trans, useLingui } from '@lingui/react/macro';
import { Badge, Button, cn } from '@pantry/ui';
import { type AuditLogEntry, AUDIT_ACTIONS, getAuditActionDescription } from '@pantry/api-client';
import {
  XMarkIcon,
  UserIcon,
  ClockIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  TagIcon,
} from '@heroicons/react/24/outline';

// Target type colors
const TARGET_TYPE_COLORS: Record<string, string> = {
  user: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  announcement: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  product: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  contribution: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  subscription: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  household: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  job: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  translation: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  system: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

interface AuditLogDetailSheetProps {
  log: AuditLogEntry;
  onClose: () => void;
}

export function AuditLogDetailSheet({ log, onClose }: AuditLogDetailSheetProps) {
  const { t, i18n } = useLingui();
  const actionDescription = AUDIT_ACTIONS[log.action as keyof typeof AUDIT_ACTIONS] || log.action;

  // Format the details for display, removing adminEmail from main view (shown separately)
  const { adminEmail, ...displayDetails } = log.details;

  return (
    <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose}>
      <div
        className="fixed right-0 top-0 h-full w-full max-w-lg bg-card border-l border-border shadow-xl animate-in slide-in-from-right duration-200 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold">
              <Trans>Audit Log Details</Trans>
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">{actionDescription}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-lg">
            <XMarkIcon className="h-5 w-5" />
            <span className="sr-only">
              <Trans>Close</Trans>
            </span>
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Action & Target */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <TagIcon className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  <Trans context="table_column">Action</Trans>
                </p>
                <p className="font-medium">{log.action}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <DocumentTextIcon className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  <Trans>Target</Trans>
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge
                    variant="secondary"
                    className={cn(
                      'font-medium capitalize',
                      TARGET_TYPE_COLORS[log.targetType] || ''
                    )}
                  >
                    {log.targetType}
                  </Badge>
                  {log.targetId && (
                    <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono">
                      {log.targetId}
                    </code>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Admin Info */}
          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <UserIcon className="h-4 w-4" />
              <Trans>Admin</Trans>
            </div>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-muted-foreground">
                  <Trans>Name</Trans>
                </p>
                <p className="font-medium">{log.admin.name || t`Not set`}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  <Trans>Email</Trans>
                </p>
                <p className="font-medium">{log.admin.email}</p>
              </div>
              {log.admin.id && (
                <div>
                  <p className="text-sm text-muted-foreground">
                    <Trans>User ID</Trans>
                  </p>
                  <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono">
                    {log.admin.id}
                  </code>
                </div>
              )}
              {!log.admin.id && (
                <p className="text-xs text-muted-foreground italic">
                  <Trans>Admin user has been deleted. Email preserved for audit trail.</Trans>
                </p>
              )}
            </div>
          </div>

          {/* Timestamp */}
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
              <ClockIcon className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                <Trans>Timestamp</Trans>
              </p>
              <p className="font-medium">{formatFullDateTime(log.createdAt, i18n.locale)}</p>
            </div>
          </div>

          {/* Request Info */}
          {(log.ipAddress || log.userAgent) && (
            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <ComputerDesktopIcon className="h-4 w-4" />
                <Trans>Request Info</Trans>
              </div>
              <div className="space-y-2">
                {log.ipAddress && (
                  <div className="flex items-center gap-2">
                    <GlobeAltIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{log.ipAddress}</span>
                  </div>
                )}
                {log.userAgent && (
                  <div>
                    <p className="text-sm text-muted-foreground">
                      <Trans>User Agent</Trans>
                    </p>
                    <p className="text-sm font-mono bg-muted rounded p-2 mt-1 break-all">
                      {log.userAgent}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Details */}
          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <DocumentTextIcon className="h-4 w-4" />
              <Trans>Action Details</Trans>
            </div>
            {Object.keys(displayDetails).length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                <Trans>No additional details</Trans>
              </p>
            ) : (
              <div className="space-y-2">
                {Object.entries(displayDetails).map(([key, value]) => (
                  <DetailRow key={key} label={formatDetailKey(key)} value={value} />
                ))}
              </div>
            )}
          </div>

          {/* Raw JSON */}
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              <Trans>View raw JSON</Trans>
            </summary>
            <pre className="mt-2 bg-muted rounded-lg p-4 overflow-x-auto text-xs font-mono">
              {JSON.stringify(log, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: unknown }) {
  // Handle different value types
  if (value === null || value === undefined) {
    return (
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-sm italic text-muted-foreground/70">
          <Trans>null</Trans>
        </p>
      </div>
    );
  }

  if (typeof value === 'boolean') {
    return (
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <Badge variant={value ? 'default' : 'secondary'}>
          {value ? <Trans>Yes</Trans> : <Trans>No</Trans>}
        </Badge>
      </div>
    );
  }

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return (
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {value.length === 0 ? (
              <span className="text-sm italic text-muted-foreground/70">
                <Trans>Empty array</Trans>
              </span>
            ) : (
              value.map((item, i) => (
                <Badge key={i} variant="secondary" className="text-xs">
                  {String(item)}
                </Badge>
              ))
            )}
          </div>
        </div>
      );
    }

    return (
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <pre className="mt-1 bg-muted/50 rounded p-2 text-xs font-mono overflow-x-auto">
          {JSON.stringify(value, null, 2)}
        </pre>
      </div>
    );
  }

  // String, number, etc.
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="font-medium">{String(value)}</p>
    </div>
  );
}

function formatFullDateTime(date: string, locale: string): string {
  const d = new Date(date);
  return d.toLocaleString(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}

function formatDetailKey(key: string): string {
  // Convert camelCase to Title Case with spaces
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}
